import {useCallback, useEffect, useState} from 'react'

type DataType = 'saved' | 'current'

type Params<T> = {
  isDataValid: (data: T) => boolean
  isDataTouched: (data: T) => boolean
  getSavedData: () => T
  setSavedData: (data: T) => void
  hint: string
}

export type UserInputStepManager<T> = {
  getData: (type?: DataType) => T
  update: React.Dispatch<React.SetStateAction<T>>
  isValid: (type?: DataType) => boolean
  isTouched: () => boolean
  save: (data?: T) => void
  reset: () => void
  hint: string
}

export const useUserInputStepManager = <T>({
  isDataValid,
  isDataTouched,
  getSavedData,
  setSavedData,
  hint,
}: Params<T>): UserInputStepManager<T> => {
  const savedData = getSavedData()

  const [data, update] = useState<T>(savedData)

  useEffect(() => {
    update(savedData)
  }, [savedData])

  const getData = useCallback(
    (type: DataType = 'current') => (type === 'current' ? data : getSavedData()),
    [data, getSavedData]
  )
  const isValid = useCallback(
    (type: DataType = 'current') => isDataValid(type === 'current' ? data : getSavedData()),
    [isDataValid, data, getSavedData]
  )
  const isTouched = useCallback(() => isDataTouched(getSavedData()), [isDataTouched, getSavedData])
  const save = useCallback(
    (passedData?: T) => setSavedData(passedData === undefined ? data : passedData),
    [setSavedData, data]
  )
  const reset = useCallback(() => setSavedData(getSavedData()), [setSavedData, getSavedData])

  return {
    getData,
    update,
    isValid,
    isTouched,
    save,
    reset,
    hint,
  }
}
